import React from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Paper,
    Grid,
    Divider,
    Alert,
    MenuItem,
    IconButton,
    Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import CartStore from "../../stores/CartStore";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import { CartItemServiceFee, CartTax } from "../../models/Cart";
import PaymentForm from "../checkout/PaymentForm";
import {
    EmptyAddress,
    Order,
    OrderAddress,
    OrderStatus,
    OtherAddress,
    PaymentMethod,
    TransactionLogStatus,
} from "../../models/Order";
import {
    EcommercePayment,
    OfflinePayment,
    SquareOnlinePayment,
} from "../../requests/OrderRequests";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ApplicationStore from "../../stores/ApplicationStore";
import { getOrderMetadata } from "../../utils/orderMetadata";
import { getErrorMessage, priceFormatter } from "../../utils/helpers";
import toast from "../../utils/toast";
import {
    CANADA_ID,
    CountryOptions,
    ProvinceOptions,
    StateOptions,
    US_ID,
} from "../../utils/addressFormSelections";
import InputField from "../form/InputField";
import CartExpiryBanner from "../cart/CartExpiryBanner";
import DialogStore from "../../stores/DialogStore";
import { CNE_GA_FILTER_DEPARTMENT_ID } from "../../constants/gaTickets";

type Props = {
    cartStore: CartStore | null;
    clientConfig: ClientConfiguration | null;
    customFieldValues: Record<string, Record<string, string>>;
    onBack: () => void;
};

function stateIdToName(countryId: number, stateId: number): string {
    if (countryId === US_ID) {
        return StateOptions().find((s) => s.id === stateId)?.name ?? "";
    } else if (countryId === CANADA_ID) {
        return ProvinceOptions().find((s) => s.id === stateId)?.name ?? "";
    }
    return "Other";
}

function countryIdToName(countryId: number): string {
    return CountryOptions().find((c) => c.id === countryId)?.name ?? "";
}

function customFieldNameToValue(
    name: string,
    customFieldValues: any,
    address: OrderAddress,
): any {
    switch (name) {
        case "address":
            return `${address.address}, ${address.city}, ${address.stateName}, ${address.countryName}`;
        case "fullName":
            return `${address.firstName} ${address.lastName}`;
        case "email":
            return address.email;
        default:
            return customFieldValues[name] || "";
    }
}

const TournamentCheckout = observer(
    ({ cartStore, customFieldValues, onBack }: Props) => {
        const [address, setAddress] = React.useState<OrderAddress>(
            EmptyAddress(),
        );
        const [ecommercePayment, setEcommercePayment] =
            React.useState<EcommercePayment>({
                discriminator: "ECOMMERCE",
                cardNum: "",
                expiryMonth: "",
                expiryYear: "",
                nameOnCard: "",
                cvv: "",
            });
        const [offlinePayment, setOfflinePayment] =
            React.useState<OfflinePayment>({
                discriminator: "OFFLINE",
                paymentMethod: PaymentMethod.CASH,
            });
        const [order, setOrder] = React.useState<Order | null>(null);
        const [loading] = React.useState(false);
        const [isPaymentProcessing, setIsPaymentProcessing] =
            React.useState(false);
        const [updatingGaTickets, setUpdatingGaTickets] = React.useState(false);
        const [fieldErrors, setFieldErrors] = React.useState<{
            [key: string]: boolean;
        }>({});

        const { executeRecaptcha } = useGoogleReCaptcha();

        // Custom field values are passed in as props from TournamentRegistrationFlow

        // Separate GA tickets from tournament registrations
        const gaTickets =
            cartStore?.cart?.items.filter(
                (item) =>
                    item.department.id === CNE_GA_FILTER_DEPARTMENT_ID,
            ) || [];
        const tournamentItems =
            cartStore?.cart?.items.filter(
                (item) =>
                    item.department.id !== CNE_GA_FILTER_DEPARTMENT_ID,
            ) || [];

        // Group GA tickets by date for display
        const gaTicketsByDate = gaTickets.reduce((acc, ticket) => {
            const date = ticket.event.title; // Assuming title contains the date
            if (!acc[date]) {
                acc[date] = {
                    event: ticket.event,
                    department: ticket.department,
                    tickets: [],
                    serviceFees: ticket.serviceFees,
                    comp: ticket.comp,
                };
            }
            acc[date].tickets.push(ticket);
            return acc;
        }, {} as Record<string, { event: any; department: any; tickets: any[]; serviceFees: any[]; comp: boolean }>);

        const handleGaQuantityChange = async (
            departmentId: number,
            increment: boolean,
        ) => {
            if (!cartStore || updatingGaTickets) return;

            setUpdatingGaTickets(true);
            try {
                if (increment) {
                    await cartStore.incrementGaQuantity(departmentId);
                } else {
                    // Check if this would remove the last ticket
                    const gaCount = gaTickets.filter(
                        (t) => t.department.id === departmentId,
                    ).length;
                    if (gaCount === 1) {
                        // Show confirmation dialog for removing last ticket
                        setUpdatingGaTickets(false); // Reset state before dialog
                        DialogStore.showAlert(
                            "Remove GA Ticket?",
                            "General admission tickets are required for event access. Are you sure you want to remove this ticket?",
                            async () => {
                                setUpdatingGaTickets(true);
                                try {
                                    await cartStore.decrementGaQuantity(
                                        departmentId,
                                    );
                                } catch (error) {
                                    toast.error("Failed to remove GA ticket");
                                } finally {
                                    setUpdatingGaTickets(false);
                                }
                            },
                        );
                        return;
                    } else {
                        await cartStore.decrementGaQuantity(departmentId);
                    }
                }
            } catch (error) {
                toast.error("Failed to update GA ticket quantity");
            } finally {
                setUpdatingGaTickets(false);
            }
        };

        const handleRemoveAllGaTickets = () => {
            if (!cartStore || gaTickets.length === 0) return;

            DialogStore.showAlert(
                "Remove All GA Tickets?",
                `You have ${gaTickets.length} general admission ticket${
                    gaTickets.length > 1 ? "s" : ""
                } in your cart. GA tickets are required for event access. Are you sure you want to remove all of them?`,
                async () => {
                    setUpdatingGaTickets(true);
                    try {
                        for (const ticket of gaTickets) {
                            await cartStore.removeTicketsFromCart([
                                ticket.ticketId,
                            ]);
                        }
                        toast.success("Removed all GA tickets from cart");
                    } catch (error) {
                        toast.error("Failed to remove GA tickets");
                    } finally {
                        setUpdatingGaTickets(false);
                    }
                },
            );
        };

        async function onSquareSubmit(
            nonce: string,
            verificationToken?: string,
        ) {
            if (!validateRequiredFields()) {
                toast.error("Please fill out all required fields.");
                return;
            }

            if (!executeRecaptcha) return;
            const token = await executeRecaptcha("checkout");
            doSquareCheckout({
                discriminator: "SQUARE_ECOMMERCE",
                squareNonce: nonce,
                verificationToken,
                reCaptchaResponse: token,
            });
        }

        function doSquareCheckout(squarePayment: SquareOnlinePayment) {
            if (!squarePayment.squareNonce || isPaymentProcessing) return;

            setIsPaymentProcessing(true);

            completeOrder(address, squarePayment, undefined)
                .then((order) => {
                    setOrder(order);
                })
                .catch(() => {
                    // Already caught by completeOrder
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        }

        async function completeOrder(
            orderAddress: OrderAddress,
            payment: EcommercePayment | SquareOnlinePayment | OfflinePayment,
            shippingAddress?: OtherAddress,
        ): Promise<Order> {
            if (!cartStore) return Promise.reject("No cart store found");

            try {
                // Build custom field data for each ticket
                const customFieldData = cartStore.itemsWithCustomFields.reduce(
                    (acc, item) => {
                        const ticketKey = `ticket-${item.ticketId}`;
                        const ticketCustomFields =
                            customFieldValues[ticketKey] || {};

                        acc[item.ticketId] =
                            item.department.customFields.reduce(
                                (fieldAcc, field) => {
                                    fieldAcc[field.id] = customFieldNameToValue(
                                        field.internalName,
                                        ticketCustomFields,
                                        address,
                                    );
                                    return fieldAcc;
                                },
                                {} as { [fieldId: number]: any },
                            );

                        return acc;
                    },
                    {} as { [ticketId: number]: { [fieldId: number]: any } },
                );

                const order = await cartStore.completeOrder(
                    orderAddress,
                    payment,
                    shippingAddress,
                    getOrderMetadata(),
                    customFieldData,
                );

                if (order.status === OrderStatus.PAYMENT_PENDING) {
                    return order;
                }

                const transactionLog = order.transactionLogs.reduce((a, b) =>
                    new Date(a.transactionDate) > new Date(b.transactionDate)
                        ? a
                        : b,
                );

                if (transactionLog.status === TransactionLogStatus.SUCCESS) {
                    cartStore.removePromoCode();
                    cartStore.removeSalesTrackingKey();
                    ApplicationStore.clearMetadata();
                    cartStore.nextCart();
                    return order;
                }

                if (transactionLog.status === TransactionLogStatus.DECLINED) {
                    throw Error("Your payment was declined.");
                }

                throw Error(
                    "An error occurred while processing transaction: " +
                        transactionLog.responseText,
                );
            } catch (err) {
                console.log(err);
                toast.error(getErrorMessage(err));
                throw err;
            }
        }

        function isValidEmail(email: string): boolean {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        }

        function validateRequiredFields(): boolean {
            const errors: { [key: string]: boolean } = {};

            if (!address.firstName) errors.firstName = true;
            if (!address.lastName) errors.lastName = true;
            if (!address.email) errors.email = true;
            else if (!isValidEmail(address.email)) errors.emailFormat = true;
            if (!address.address) errors.address = true;
            if (!address.city) errors.city = true;
            if (!address.postal) errors.postal = true;
            if (!address.countryId) errors.countryId = true;
            if (!address.stateId) errors.stateId = true;

            setFieldErrors(errors);

            return Object.keys(errors).length === 0;
        }


        if (order) {
            return (
                <Box>
                    <Card
                        sx={(theme) => ({
                            mt: 2,
                            backgroundColor: `${theme.palette.primary.light}60`,
                        })}
                    >
                        <CardContent>
                            <Typography variant="h5" textAlign="center">
                                Registration Successful!
                            </Typography>
                            <Typography
                                variant="body1"
                                textAlign="center"
                                sx={{ mt: 1 }}
                            >
                                Your tournament registration was successful.
                                Please check your email for confirmation
                                details.
                            </Typography>
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{ mt: 2 }}
                                color="text.secondary"
                            >
                                Order ID: {order.id}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            );
        }

        return (
            <Box>
                {cartStore?.cartExpiresInSeconds && (
                    <Box sx={{ mb: 2 }}>
                        <CartExpiryBanner
                            expiresInSeconds={cartStore.cartExpiresInSeconds}
                            onTimeUp={() => {
                                toast.error(
                                    "Cart has expired. Please start over.",
                                );
                                cartStore.clearCart(false);
                            }}
                            prefix="Your registration expires in"
                            suffix="Please complete your purchase before time runs out."
                        />
                    </Box>
                )}

                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h4" gutterBottom textAlign="center">
                        Complete Registration
                    </Typography>
                    <Typography
                        variant="body1"
                        textAlign="center"
                        color="text.secondary"
                    >
                        Review your cart and complete your tournament
                        registration.
                    </Typography>
                </Paper>

                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Order Summary
                        </Typography>

                        {/* Tournament Registrations - Read Only */}
                        {tournamentItems.length > 0 && (
                            <>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, mt: 2 }}
                                >
                                    Tournament Registrations
                                </Typography>
                                {[...tournamentItems]
                                    .sort((a, b) => {
                                        const weightA =
                                            a.department.orderWeight || 99;
                                        const weightB =
                                            b.department.orderWeight || 99;

                                        if (weightA !== weightB) {
                                            return weightA - weightB;
                                        }

                                        // If weights are the same, sort by name case-insensitive
                                        return a.department.name
                                            .toLowerCase()
                                            .localeCompare(
                                                b.department.name.toLowerCase(),
                                            );
                                    })
                                    .map((item, index) => (
                                        <Box key={item.ticketId} sx={{ py: 1 }}>
                                            <Grid
                                                container
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                            >
                                                <Grid item xs={8}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {item.department.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {item.event.title}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={4}
                                                    textAlign="right"
                                                >
                                                    <Typography variant="body2">
                                                        {priceFormatter(
                                                            item.department
                                                                .price,
                                                        )}
                                                    </Typography>
                                                    {/* Service fees for this item */}
                                                    {item.serviceFees &&
                                                        item.serviceFees
                                                            .length > 0 &&
                                                        !item.comp && (
                                                            <Box
                                                                sx={{
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                {item.serviceFees.map(
                                                                    (
                                                                        fee: CartItemServiceFee,
                                                                    ) => (
                                                                        <Typography
                                                                            key={
                                                                                fee.id
                                                                            }
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            display="block"
                                                                        >
                                                                            +{" "}
                                                                            {priceFormatter(
                                                                                fee.amount,
                                                                            )}{" "}
                                                                            {
                                                                                fee.name
                                                                            }
                                                                        </Typography>
                                                                    ),
                                                                )}
                                                            </Box>
                                                        )}
                                                </Grid>
                                            </Grid>
                                            {index <
                                                [...tournamentItems].length -
                                                    1 && (
                                                <Divider sx={{ my: 1 }} />
                                            )}
                                        </Box>
                                    ))}
                            </>
                        )}

                        {/* GA Tickets - Editable */}
                        {gaTickets.length > 0 && (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                        mt: 3,
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        General Admission Tickets (
                                        {gaTickets.length})
                                    </Typography>
                                    {gaTickets.length > 1 && (
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleRemoveAllGaTickets}
                                            disabled={updatingGaTickets}
                                        >
                                            Remove All
                                        </Button>
                                    )}
                                </Box>
                                {gaTickets.length > 0 && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Guests only need 1 GA ticket per day for
                                        event access.
                                    </Alert>
                                )}
                                {Object.entries(gaTicketsByDate).map(
                                    ([date, group], index) => (
                                        <Box key={date} sx={{ py: 1 }}>
                                            <Grid
                                                container
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Grid item xs={7}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {group.department.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {group.event.title}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleGaQuantityChange(
                                                                    group
                                                                        .department
                                                                        .id,
                                                                    false,
                                                                )
                                                            }
                                                            disabled={
                                                                updatingGaTickets
                                                            }
                                                            sx={{
                                                                padding: 0.5,
                                                            }}
                                                        >
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                minWidth:
                                                                    "20px",
                                                                textAlign:
                                                                    "center",
                                                            }}
                                                        >
                                                            {
                                                                group.tickets
                                                                    .length
                                                            }
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleGaQuantityChange(
                                                                    group
                                                                        .department
                                                                        .id,
                                                                    true,
                                                                )
                                                            }
                                                            disabled={
                                                                updatingGaTickets
                                                            }
                                                            sx={{
                                                                padding: 0.5,
                                                            }}
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={3}
                                                    textAlign="right"
                                                >
                                                    <Typography variant="body2">
                                                        {priceFormatter(
                                                            group.department
                                                                .price *
                                                                group.tickets
                                                                    .length,
                                                        )}
                                                    </Typography>
                                                    {/* Service fees */}
                                                    {group.serviceFees &&
                                                        group.serviceFees
                                                            .length > 0 &&
                                                        !group.comp && (
                                                            <Box
                                                                sx={{
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                {group.serviceFees.map(
                                                                    (
                                                                        fee: CartItemServiceFee,
                                                                    ) => (
                                                                        <Typography
                                                                            key={
                                                                                fee.id
                                                                            }
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            display="block"
                                                                        >
                                                                            +{" "}
                                                                            {priceFormatter(
                                                                                fee.amount *
                                                                                    group
                                                                                        .tickets
                                                                                        .length,
                                                                            )}{" "}
                                                                            {
                                                                                fee.name
                                                                            }
                                                                        </Typography>
                                                                    ),
                                                                )}
                                                            </Box>
                                                        )}
                                                </Grid>
                                            </Grid>
                                            {index <
                                                Object.keys(gaTicketsByDate)
                                                    .length -
                                                    1 && (
                                                <Divider sx={{ my: 1 }} />
                                            )}
                                        </Box>
                                    ),
                                )}
                            </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Show subtotal if there are taxes or auth fees */}
                        {(cartStore?.cart?.taxes &&
                            cartStore.cart.taxes.length > 0) ||
                        (cartStore?.authFee && cartStore.authFee > 0) ? (
                            <>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <Grid item>
                                        <Typography variant="body2">
                                            Subtotal
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">
                                            {priceFormatter(
                                                cartStore?.subtotal || 0,
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {/* Auth fee */}
                                {cartStore?.authFee !== null &&
                                    cartStore.authFee > 0 && (
                                        <Grid
                                            container
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{ mb: 1 }}
                                        >
                                            <Grid item>
                                                <Typography variant="body2">
                                                    Transaction Fee
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">
                                                    {priceFormatter(
                                                        cartStore.authFee,
                                                    )}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )}

                                {/* Tax breakdown */}
                                {cartStore?.cart?.taxes &&
                                    cartStore.cart.taxes.map((tax: CartTax) => (
                                        <Grid
                                            container
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{ mb: 1 }}
                                            key={tax.id}
                                        >
                                            <Grid item>
                                                <Typography variant="body2">
                                                    {tax.name}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">
                                                    {priceFormatter(tax.amount)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    ))}

                                <Divider sx={{ my: 1 }} />
                            </>
                        ) : null}

                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item>
                                <Typography variant="h6" fontWeight="bold">
                                    Total
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="h6" fontWeight="bold">
                                    {priceFormatter(cartStore?.total || 0)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Billing Information
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="First Name"
                                    value={address.firstName}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            firstName: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (fieldErrors.firstName) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                firstName: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={fieldErrors.firstName}
                                    helperText={
                                        fieldErrors.firstName
                                            ? "First name is required"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="Last Name"
                                    value={address.lastName}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            lastName: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (fieldErrors.lastName) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                lastName: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={fieldErrors.lastName}
                                    helperText={
                                        fieldErrors.lastName
                                            ? "Last name is required"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    label="Email"
                                    type="email"
                                    value={address.email}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            email: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (
                                            fieldErrors.email ||
                                            fieldErrors.emailFormat
                                        ) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                email: false,
                                                emailFormat: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={
                                        fieldErrors.email ||
                                        fieldErrors.emailFormat
                                    }
                                    helperText={
                                        fieldErrors.email
                                            ? "Email is required"
                                            : fieldErrors.emailFormat
                                            ? "Please enter a valid email address (e.g., test@test.com)"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputField
                                    label="Address"
                                    value={address.address}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            address: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (fieldErrors.address) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                address: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={fieldErrors.address}
                                    helperText={
                                        fieldErrors.address
                                            ? "Address is required"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="City"
                                    value={address.city}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            city: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (fieldErrors.city) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                city: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={fieldErrors.city}
                                    helperText={
                                        fieldErrors.city
                                            ? "City is required"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="Postal Code"
                                    value={address.postal || ""}
                                    onChange={(e) => {
                                        setAddress({
                                            ...address,
                                            postal: e.target.value,
                                        });
                                        // Clear error when user types
                                        if (fieldErrors.postal) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                postalCode: false,
                                            }));
                                        }
                                    }}
                                    fullWidth
                                    required
                                    error={fieldErrors.postal}
                                    helperText={
                                        fieldErrors.postal
                                            ? "Postal code is required"
                                            : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="Country"
                                    fullWidth
                                    select
                                    required
                                    value={address.countryId || ""}
                                    onChange={(e) => {
                                        const id = parseInt(
                                            e.target.value || "0",
                                            10,
                                        );
                                        setAddress({
                                            ...address,
                                            countryId: id,
                                            countryName: countryIdToName(id),
                                            stateId: null,
                                        });
                                        // Clear error when user selects
                                        if (fieldErrors.countryId) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                countryId: false,
                                            }));
                                        }
                                    }}
                                    error={fieldErrors.countryId}
                                    helperText={
                                        fieldErrors.countryId
                                            ? "Country is required"
                                            : ""
                                    }
                                >
                                    <MenuItem value="">
                                        Select a country
                                    </MenuItem>
                                    {CountryOptions().map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputField
                                    label="Province/State"
                                    fullWidth
                                    select
                                    required
                                    value={address.stateId || ""}
                                    disabled={!address.countryId}
                                    onChange={(e) => {
                                        const id = parseInt(
                                            e.target.value || "0",
                                            10,
                                        );
                                        setAddress({
                                            ...address,
                                            stateId: id,
                                            stateName: stateIdToName(
                                                address.countryId || 0,
                                                id,
                                            ),
                                        });
                                        // Clear error when user selects
                                        if (fieldErrors.stateId) {
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                stateId: false,
                                            }));
                                        }
                                    }}
                                    error={fieldErrors.stateId}
                                    helperText={
                                        fieldErrors.stateId
                                            ? "Province/State is required"
                                            : ""
                                    }
                                >
                                    <MenuItem value="">
                                        Select a state/province
                                    </MenuItem>
                                    {(address.countryId === US_ID
                                        ? StateOptions()
                                        : address.countryId === CANADA_ID
                                        ? ProvinceOptions()
                                        : [{ id: 66, name: "Other" }]
                                    ).map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name}
                                        </MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        {cartStore && (
                            <PaymentForm
                                address={address}
                                cartStore={cartStore}
                                total={cartStore?.total || 0}
                                ecommercePayment={ecommercePayment}
                                setEcommercePayment={setEcommercePayment}
                                offlinePayment={offlinePayment}
                                setOfflinePayment={setOfflinePayment}
                                onSquareSubmit={onSquareSubmit}
                                onStripeSubmit={() => {
                                    return new Promise((resolve) => {
                                        resolve("");
                                    });
                                }}
                                onStripeError={() => {}}
                                onStripeSuccess={() => {}}
                                loading={loading || cartStore?.loading || false}
                                onIframeSubmit={() => {
                                    completeOrder(address, offlinePayment).then(
                                        (order) => {
                                            setOrder(order);
                                        },
                                    );
                                }}
                                order={order}
                                overridePurchaseText="Complete Registration"
                            />
                        )}
                    </CardContent>
                </Card>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        mb: 4,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        disabled={loading || isPaymentProcessing}
                    >
                        Back to Registration Details
                    </Button>
                </Box>
            </Box>
        );
    },
);

export default TournamentCheckout;
